import type { JSONContent } from "@tiptap/core";
import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";
import Fetcher from "../Fetcher";
import z, { ZodError } from "zod";

// Shared Fetcher instance for T&C endpoints.
const f = Fetcher.default();

export interface DashboardStatsResponse {
    kpis: {
        openRfqs: number;
        awaitingQuotes: number;
        quotesThisPeriod: number;
        avgCycleTimeDays: number | null;
    };
    attentionRequired: {
        overdueRfqs: Array<{
            uniquePublicId: string;
            title: string;
            submissionDeadline: string | null;
            vendorCount: number;
            quoteCount: number;
        }>;
        stalledRfqs: Array<{
            uniquePublicId: string;
            title: string;
            lastActivityAt: string;
            daysSinceActivity: number;
        }>;
        quoteReminders: Array<{
            mailboxId: number;
            vendorName: string;
            rfqPublicId: string;
            rfqTitle: string;
            daysSinceSent: number;
            lastReminderAt: string | null;
        }>;
        unreadCount: number;
        unreadMessages: Array<{
            id: number;
            vendorName: string;
            rfqTitle: string;
            rfqPublicId: string;
            mailboxId: number;
            preview: string;
            timestamp: string;
        }>;
    };
    upcomingDeadlines: Array<{
        uniquePublicId: string;
        title: string;
        submissionDeadline: string;
    }>;
    pipeline: {
        draft: number;
        sent: number;
        quoted: number;
        closed: number;
        rfqs: Array<{
            uniquePublicId: string;
            title: string;
            updatedAt: string;
            vendorCount: number;
            quoteCount: number;
            status: "draft" | "sent" | "quoted" | "closed";
        }>;
    };
    recentActivity: Array<{
        type: "quote_received" | "rfq_closed" | "rfq_sent" | "po_sent" | "po_acknowledged";
        // Empty string for a standalone PO, which has no originating RFQ.
        rfqPublicId: string;
        rfqTitle: string;
        vendorName?: string;
        grandTotal?: number;
        vendorCount?: number;
        mailboxId?: number;
        purchaseOrderId?: number;
        poNumber?: string;
        timestamp: string;
    }>;
    teamWorkload: Array<{
        userId: number;
        userName: string;
        openRfqs: number;
        quotesReceived: number;
        // Awarded but no PO sent — the member's own move.
        awaitingPO: number;
        // PO sent, vendor hasn't acknowledged — waiting on the vendor.
        awaitingAcknowledgement: number;
        // The two summed; what the Chase column shows.
        chaseCount: number;
        // POs sent in the window, attributed to the RFQ assignee. Standalone POs have no
        // assignee and are not counted here.
        posSent: number;
    }>;
    // Kept separate from teamWorkload: this spans its own window, and ordersMeasured counts
    // cycles measured rather than POs sent.
    buyerProcessingTime: {
        windowDays: number;
        buyers: Array<{
            userId: number;
            userName: string;
            // Average RFQ sent -> PO sent, weekends excluded, in milliseconds. Null when the
            // buyer had no RFQ reach a PO in the window.
            avgBusinessMs: number | null;
            ordersMeasured: number;
        }>;
    };
    monthlyTrends: Array<{
        month: string;
        year: number;
        rfqsCreated: number;
        rfqsClosed: number;
        quotesReceived: number;
        avgCycleTime: number | null;
    }>;
    cycleTimeByMonth: Record<string, Array<{
        uniquePublicId: string;
        title: string;
        createdAt: string;
        closedAt: string;
        cycleTimeDays: number;
    }>>;
    dailyTrends: Array<{
        date: string;
        dayOfMonth: number;
        month: string;
        rfqsCreated: number;
        rfqsClosed: number;
        quotesReceived: number;
    }>;
    communicationStats: {
        messagesSent: number;
        messagesReceived: number;
        messagesRead: number;
    };
}

export type TeamMember = {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    deactivatedAt: string | null;
    iconBackgroundColorClass: string;
};

export type UserInvite = {
    id: number;
    teamId: number;
    emailAddress: string;
    createdAt: string;
};

export type InternalUser = {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
};

export type InternalAddress = {
    firstName: string | null;
    lastName: string | null;
    email: string;
};

export type TeamInfo = {
    teamName: string;
    paidUsers: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
    }[];
    internalUsers: InternalUser[];
    approvedDomains: {
        id: number;
        domain: string;
    }[];
    invites: {
        id: number;
        teamId: number;
        emailAddress: string;
        createdAt: string;
    }[];
    // True when the team requires SSO — members are provisioned via the IdP, so invites are disabled.
    isSsoRequired: boolean;
};

// Team-wide instruction prompts that steer AI-generated content (e.g. RFQ
// subject lines). One prompt per (type, team). Matches the purchaser-api
// `team_prompt` model / TeamPromptType enum.
export type TeamPromptType = "RFQ_SUBJECT" | "SENSITIVE_DATA_DETECTION";

export type TeamPrompt = {
    type: TeamPromptType;
    prompt: string;
    isEnabled: boolean;
    updatedAt: string;
    teamId: number;
    updatedByUserId: number;
    // Only present when the API includes the relation; optional so the client
    // works whether or not the GET is enriched. Falls back to resolving the
    // name from the team-members list.
    updatedByUser?: { id: number; first_name: string; last_name: string } | null;
};

// --- Terms & Conditions types ---

/** The team's T&C configuration, including the uploaded file and optional exclusion attribute. */
export type TermsAndConditions = {
    id: number;
    teamId: number;
    fileId: number;
    vendorAttributeId: number | null;
    file: {
        id: number;
        originalFilename: string;
        mimeType: string;
        fileSizeBytes: number;
    };
    vendorAttribute: {
        id: number;
        name: string;
    } | null;
};

/** Response from the for-vendor endpoint: the T&C file to attach, or an exclusion notice. */
export type TermsAndConditionsForVendor = {
    termsAndConditions: {
        fileId: number;
        originalFilename: string;
        mimeType: string;
        fileSizeBytes: number;
    } | null;
    excluded: boolean;
};

export default {
    getTeamMembers: (): Promise<TeamMember[]> => fetch(`${PURCHASER_API_BASE_URL}/team/members`, { headers: getHeaders() }).then(res => res.json()),

    getTeamPrompt: async (type: TeamPromptType): Promise<TeamPrompt | null> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/team/prompts/${type}`, { headers: getHeaders() });
        if (!res.ok) throw new Error("Failed to load workspace instruction");
        // The route sends the prompt object, or null when none is set yet —
        // which serializes to an empty body, so guard before parsing.
        const text = await res.text();
        return text ? (JSON.parse(text) as TeamPrompt) : null;
    },

    upsertTeamPrompt: async (payload: { type: TeamPromptType; prompt: string; isEnabled: boolean }): Promise<TeamPrompt> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/team/prompts`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) {
            // The API returns { err }. Its text is authoritative (e.g. the
            // prompt-safety threat message), so surface it verbatim — just
            // strip a leading "Error: " that stringified Errors pick up.
            const message = typeof data?.err === "string" ? data.err.replace(/^Error:\s*/i, "") : "Failed to save workspace instruction";
            throw new Error(message);
        }
        return data.prompt as TeamPrompt;
    },

    deleteTeamPrompt: async (type: TeamPromptType): Promise<void> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/team/prompts/${type}`, {
            method: "DELETE",
            headers: getHeaders(),
            // getHeaders() sets Content-Type: application/json, and Fastify rejects an
            // empty body with that header (FST_ERR_CTP_EMPTY_JSON_BODY) — send {}.
            body: JSON.stringify({}),
        });
        // 404 means it's already gone — treat as a successful no-op so a
        // double-delete or race doesn't surface a spurious error.
        if (!res.ok && res.status !== 404) {
            const data = await res.json().catch(() => null);
            const message = typeof data?.err === "string" ? data.err.replace(/^Error:\s*/i, "") : "Failed to delete workspace instruction";
            throw new Error(message);
        }
    },

    getUserInvites: (): Promise<UserInvite[]> => fetch(`${PURCHASER_API_BASE_URL}/team/invite`, { headers: getHeaders() }).then(res => res.json()),

    getTeamInfo: async (): Promise<TeamInfo> => {
        const url = `${PURCHASER_API_BASE_URL}/team/info`;
        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok || res.status !== 200) {
            throw new Error("Failed to get team info");
        }
        return res.json();
    },

    changeTeamName: async (name: string): Promise<void> => {
        const url = `${PURCHASER_API_BASE_URL}/team/info`;
        const res = await fetch(url, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({ name }),
        });

        if (!res.ok && res.status !== 204) {
            throw new Error("Failed to update team name");
        }
    },

    inviteUsers: async (emails: string[]): Promise<{ sent: string[]; failed: { email: string; reason: string }[] }> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/team/invite`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({ emails }),
        });
        const data = await res.json();
        if (!res.ok || data?.error) {
            throw new Error("Failed to send invite");
        }
        return data;
    },

    deleteUserInvite: (id: number) =>
        fetch(`${PURCHASER_API_BASE_URL}/team/invite`, {
            headers: getHeaders(),
            method: "DELETE",
            body: JSON.stringify({ id }),
        }).then(res => res.json()),

    requestFeatureAccess: (params: { featureName: string; userMessage?: string; userProperties?: Record<string, any> }) =>
        fetch(`${PURCHASER_API_BASE_URL}/team/feature-access`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify(params),
        }).then(res => res.json()),

    getUserGreeting: () => fetch(`${PURCHASER_API_BASE_URL}/team/greet`, { headers: getHeaders() }).then(res => res.json()),

    getRecentEntities: () => fetch(`${PURCHASER_API_BASE_URL}/team/recent-entities`, { headers: getHeaders() }).then(res => res.json()),

    getHelpArticles: () => {
        return fetch(`https://purchaser.ai/api/featured-docs`, {
            method: "GET",
        }).then(res => {
            if (!res.ok) {
                return [];
            }
            return res.json();
        });
    },

    getNewFeatures: () => {
        return fetch(`https://purchaser.ai/api/new-features`, {
            method: "GET",
        }).then(res => {
            if (!res.ok) {
                return [];
            }
            return res.json();
        });
    },

    getProTips: () => {
        return fetch(`https://purchaser.ai/api/pro-tips`, {
            method: "GET",
        }).then(res => {
            if (!res.ok) {
                return [];
            }
            return res.json();
        });
    },

    getTeamStats: async () => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/team/stats`, {
            method: "GET",
            headers: getHeaders(),
        });
        if (!res.ok) {
            throw new Error("Failed to get team stats");
        }
        return (await res.json()) as Promise<{
            rfqsSent: {
                value: number;
                vsLast7Days: number;
            };
            quoteCompares: {
                value: number;
                vsLast7Days: number;
            };
            draftRfqs: {
                value: number;
                vsLast7Days: number;
            };
        }>;
    },

    getDashboardStats: async (params?: { userId?: number; days?: number }) => {
        const searchParams = new URLSearchParams();
        if (params?.userId) searchParams.set("userId", params.userId.toString());
        if (params?.days) searchParams.set("days", params.days.toString());

        const queryString = searchParams.toString();
        const url = `${PURCHASER_API_BASE_URL}/team/dashboard-stats${queryString ? `?${queryString}` : ""}`;

        const res = await fetch(url, {
            method: "GET",
            headers: getHeaders(),
        });
        if (!res.ok) {
            throw new Error("Failed to get dashboard stats");
        }
        return (await res.json()) as DashboardStatsResponse;
    },

    sendTestEmail: (data: { emailAddress: string; content: JSONContent; fileUploadIds?: number[] }) =>
        fetch(`${PURCHASER_API_BASE_URL}/team/send-test-email`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify(data),
        }).then(res => res.json()),

    getCalendarEvents: async (params?: { start?: string; end?: string }) => {
        const searchParams = new URLSearchParams();
        if (params?.start) searchParams.set("start", params.start);
        if (params?.end) searchParams.set("end", params.end);

        const queryString = searchParams.toString();
        const url = `${PURCHASER_API_BASE_URL}/team/calendar-events${queryString ? `?${queryString}` : ""}`;

        const res = await fetch(url, {
            method: "GET",
            headers: getHeaders(),
        });
        if (!res.ok) {
            throw new Error("Failed to get calendar events");
        }
        return (await res.json()) as CalendarEventsResponse;

    },

    // Returns combined list of names and email addresses of internal users (paid) and team colleagues
    getInternalAddresses: async (params?: { search?: string }): Promise<{ data: InternalAddress[] }> => {
        const searchParams = new URLSearchParams();
        if (params?.search) searchParams.set("search", params.search);
        const queryString = searchParams.toString();
        const url = `${PURCHASER_API_BASE_URL}/team/internal-addresses${queryString ? `?${queryString}` : ""}`;
        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) throw new Error("Failed to get internal addresses");
        return res.json();
    },

    addInternalUser: async (payload: { emailAddress: string; firstName: string | null; lastName: string | null }): Promise<void> => {
        const url = `${PURCHASER_API_BASE_URL}/team-colleagues`;
        const res = await fetch(url, { headers: getHeaders(), method: "POST", body: JSON.stringify(payload) });
        if (!res.ok) throw new Error("Failed to add internal user");
    },

    updateInternalUser: async (id: number, payload: { firstName: string | null; lastName: string | null }): Promise<void> => {
        const url = `${PURCHASER_API_BASE_URL}/team-colleagues/${id}`;
        const res = await fetch(url, { headers: getHeaders(), method: "PATCH", body: JSON.stringify(payload) });
        if (!res.ok) throw new Error("Failed to add internal user");
    },

    deleteInternalUser: async (id: number): Promise<void> => {
        const url = `${PURCHASER_API_BASE_URL}/team-colleagues/${id}`;
        const res = await fetch(url, { headers: getHeaders(), method: "DELETE" });
        if (!res.ok) throw new Error("Failed to delete internal user");
    },

    addApprovedDomain: async (domain: string): Promise<void> => {
        try {
            // The backend will do the hard checks but this is a small sanity check before we make the request
            const softValidDomain = z
                .string()
                .min(3)
                .refine(val => val.includes("."), {
                    message: "Domain must contain a dot.",
                })
                .refine(val => val.split(".").filter(s => s !== "").length > 1, {
                    message: "Invalid domain structure.",
                })
                .refine(val => !val.includes(" "), {
                    message: "Domain should not contain spaces.",
                })
                .parse(domain);

            const url = `${PURCHASER_API_BASE_URL}/approved-domains`;
            const res = await fetch(url, { headers: getHeaders(), method: "POST", body: JSON.stringify({ domain: softValidDomain }) });

            if (!res.ok) {
                throw new Error("Error adding approved domain");
            }

            if (res.status < 299) return;

            const body = await res.json();
        } catch (e) {
            if (e instanceof ZodError) {
                throw new Error(e.issues[0].message);
            } else {
                throw e;
            }
        }
    },

    deleteApprovedDomain: async (id: number): Promise<void> => {
        const url = `${PURCHASER_API_BASE_URL}/approved-domains/${id}`;
        const res = await fetch(url, { headers: getHeaders(), method: "DELETE" });

        if (!res.ok) throw new Error("Failed to delete approved domain");

        return res.json();
    },

    // --- Terms & Conditions ---

    /** Fetches the team's current T&C config (file + optional exclusion attribute). */
    getTermsAndConditions: () =>
        f.get<{ termsAndConditions: TermsAndConditions | null }>("/team/terms-and-conditions"),

    /** Sets or replaces the team's T&C file via upsert. */
    setTermsAndConditions: (body: { fileId: number; vendorAttributeId?: number }) =>
        f.post<{ termsAndConditions: TermsAndConditions }>("/team/terms-and-conditions", { body }),

    /** Updates which vendor attribute is used for the exclusion rule. Pass null to clear. */
    updateTermsAndConditions: (id: number, body: { vendorAttributeId: number | null }) =>
        f.patch<{ termsAndConditions: TermsAndConditions }>(`/team/terms-and-conditions/${id}`, { body }),

    /** Removes the T&C config (does not delete the underlying uploaded file). */
    deleteTermsAndConditions: (id: number) =>
        f.delete<void>(`/team/terms-and-conditions/${id}`, { body: {} }),

    /** Returns the T&C file for a specific vendor, accounting for exclusion rules. */
    getTermsAndConditionsForVendor: (vendorRelationshipId: number) =>
        f.get<TermsAndConditionsForVendor>(`/team/terms-and-conditions/vendor-relationships/${vendorRelationshipId}`),
};

export type CalendarEventType = "rfq_created" | "rfq_sent" | "deadline" | "reminder_scheduled" | "reminder_sent" | "quote_received" | "po_sent" | "po_acknowledged";

export interface CalendarEvent {
    id: string;
    type: CalendarEventType;
    title: string;
    description: string;
    date: string;
    rfqPublicId?: string;
    mailboxId?: number;
    purchaseOrderId?: number;
}

export interface CalendarEventsResponse {
    events: CalendarEvent[];
    dateRange: {
        start: string;
        end: string;
    };
}
